<template>
  <div class="domain-manager">
    <div class="manager-container">
      <h2 class="page-title">Domain Manager</h2>
      <p class="page-description">Manage the list of domains, descriptions, and owners</p>
      
      <div class="add-domain-section">
        <h3>Add New Domain</h3>
        <div class="add-form">
          <div class="form-row">
            <div class="form-group">
              <label for="new-domain">Domain:</label>
              <input
                id="new-domain"
                v-model="newDomain.domain"
                type="text"
                placeholder="example.com"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="new-description">Description:</label>
              <input
                id="new-description"
                v-model="newDomain.description"
                type="text"
                placeholder="Company description"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="new-owner">Owner:</label>
              <input
                id="new-owner"
                v-model="newDomain.owner"
                type="text"
                placeholder="Owner name"
                class="form-input"
              />
            </div>
            <button @click="addDomain" class="add-button" :disabled="!isValidNewDomain">
              Add Domain
            </button>
          </div>
        </div>
      </div>

      <div class="domains-list-section">
        <h3>Current Domains ({{ domainList.length }})</h3>
        <div v-if="domainList.length === 0" class="empty-state">
          <p>No domains in the list. Add some domains to get started.</p>
        </div>
        <div v-else class="domains-table">
          <div class="table-header">
            <div class="col-domain">Domain</div>
            <div class="col-description">Description</div>
            <div class="col-owner">Owner</div>
            <div class="col-actions">Actions</div>
          </div>
          <div
            v-for="(domain, index) in domainList"
            :key="index"
            class="table-row"
          >
            <div class="col-domain">
              <input
                v-if="editingIndex === index"
                v-model="editingDomain.domain"
                class="edit-input"
              />
              <span v-else>{{ domain.domain }}</span>
            </div>
            <div class="col-description">
              <input
                v-if="editingIndex === index"
                v-model="editingDomain.description"
                class="edit-input"
              />
              <span v-else>{{ domain.description }}</span>
            </div>
            <div class="col-owner">
              <input
                v-if="editingIndex === index"
                v-model="editingDomain.owner"
                class="edit-input"
              />
              <span v-else>{{ domain.owner }}</span>
            </div>
            <div class="col-actions">
              <div v-if="editingIndex === index" class="edit-actions">
                <button @click="saveEdit" class="save-button">Save</button>
                <button @click="cancelEdit" class="cancel-button">Cancel</button>
              </div>
              <div v-else class="normal-actions">
                <button @click="startEdit(index)" class="edit-button">Edit</button>
                <button @click="deleteDomain(index)" class="delete-button">Delete</button>
              </div>
            </div>
          </div>
        </div>
      </div>



      <div v-if="message" class="message-section">
        <div :class="['message', message.type]">
          {{ message.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DomainManager',
  data() {
    return {
      domainList: [],
      newDomain: {
        domain: '',
        description: '',
        owner: ''
      },
      editingIndex: -1,
      editingDomain: {
        domain: '',
        description: '',
        owner: ''
      },
      message: null
    };
  },
  computed: {
    isValidNewDomain() {
      return this.newDomain.domain.trim() && 
             this.newDomain.description.trim() && 
             this.newDomain.owner.trim();
    }
  },
  async mounted() {
    await this.loadDomainList();
  },
  methods: {
    async loadDomainList() {
      try {
        // Add cache-busting parameter to ensure fresh data
        const timestamp = new Date().getTime();
        const response = await fetch(`/api/domains?t=${timestamp}`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        const data = await response.json();
        
        if (data.success) {
          this.domainList = data.domains;
          this.showMessage('Domain list loaded successfully', 'success');
        } else {
          throw new Error(data.error || 'Failed to load domains');
        }
      } catch (error) {
        console.error('Error loading domain list:', error);
        this.showMessage('Failed to load domain list', 'error');
      }
    },
    
    parseDomainList(text) {
      const lines = text.trim().split('\n');
      return lines.map(line => {
        const parts = line.trim().split(/\s+/);
        if (parts.length >= 3) {
          return {
            domain: parts[0],
            description: parts.slice(1, -1).join(' '),
            owner: parts[parts.length - 1]
          };
        }
        return null;
      }).filter(item => item !== null);
    },
    
    async addDomain() {
      if (!this.isValidNewDomain) return;
      
      try {
        const response = await fetch('/api/domains', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(this.newDomain)
        });
        
        const data = await response.json();
        
        if (data.success) {
          this.newDomain = { domain: '', description: '', owner: '' };
          this.showMessage('Domain added successfully', 'success');
          // Reload the domain list to get the updated data with _id
          await this.loadDomainList();
          // Notify other components that domains have been updated
          localStorage.setItem('domainsUpdated', Date.now().toString());
        } else {
          throw new Error(data.error || 'Failed to add domain');
        }
      } catch (error) {
        console.error('Error adding domain:', error);
        this.showMessage('Failed to add domain: ' + error.message, 'error');
      }
    },
    
    startEdit(index) {
      this.editingIndex = index;
      this.editingDomain = { ...this.domainList[index] };
    },
    
    async saveEdit() {
      if (!this.editingDomain.domain.trim() || 
          !this.editingDomain.description.trim() || 
          !this.editingDomain.owner.trim()) {
        this.showMessage('All fields are required', 'error');
        return;
      }
      
      try {
        const domainId = this.domainList[this.editingIndex]._id;
        const response = await fetch(`/api/domains/${domainId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            domain: this.editingDomain.domain,
            description: this.editingDomain.description,
            owner: this.editingDomain.owner
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          this.editingIndex = -1;
          this.showMessage('Domain updated successfully', 'success');
          // Reload the domain list to get the updated data
          await this.loadDomainList();
          // Notify other components that domains have been updated
          localStorage.setItem('domainsUpdated', Date.now().toString());
        } else {
          throw new Error(data.error || 'Failed to update domain');
        }
      } catch (error) {
        console.error('Error updating domain:', error);
        this.showMessage('Failed to update domain: ' + error.message, 'error');
      }
    },
    
    cancelEdit() {
      this.editingIndex = -1;
      this.editingDomain = { domain: '', description: '', owner: '' };
    },
    
    async deleteDomain(index) {
      if (confirm('Are you sure you want to delete this domain?')) {
        try {
          const domainId = this.domainList[index]._id;
          const response = await fetch(`/api/domains/${domainId}`, {
            method: 'DELETE'
          });
          
          const data = await response.json();
          
          if (data.success) {
            this.showMessage('Domain deleted successfully', 'success');
            // Reload the domain list to get the updated data
            await this.loadDomainList();
            // Notify other components that domains have been updated
            localStorage.setItem('domainsUpdated', Date.now().toString());
          } else {
            throw new Error(data.error || 'Failed to delete domain');
          }
        } catch (error) {
          console.error('Error deleting domain:', error);
          this.showMessage('Failed to delete domain: ' + error.message, 'error');
        }
      }
    },
    
    
    showMessage(text, type) {
      this.message = { text, type };
      setTimeout(() => {
        this.message = null;
      }, 3000);
    }
  }
};
</script>

<style scoped>
.domain-manager {
  max-width: 1200px;
  margin: 0 auto;
}

.manager-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 2rem;
}

.page-title {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 2rem;
}

.page-description {
  color: #7f8c8d;
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

.add-domain-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #ecf0f1;
}

.add-domain-section h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.form-row {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.add-button {
  height: fit-content;
  align-self: flex-end;
  margin-top: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.form-group label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.form-input:focus {
  outline: none;
  border-color: #3498db;
}

.add-button {
  background: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  height: fit-content;
}

.add-button:hover:not(:disabled) {
  background: #229954;
}

.add-button:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.domains-list-section h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #7f8c8d;
}

.domains-table {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 180px;
  background: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
}

.table-header > div {
  padding: 1rem;
  border-right: 1px solid #ddd;
}

.table-header > div:last-child {
  border-right: none;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 180px;
  border-bottom: 1px solid #ddd;
  transition: background-color 0.3s;
}

.table-row:hover {
  background: #f8f9fa;
}

.table-row > div {
  padding: 1rem;
  border-right: 1px solid #ddd;
  display: flex;
  align-items: center;
}

.table-row > div:last-child {
  border-right: none;
}

.edit-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
}

.normal-actions {
  display: flex;
  gap: 0.5rem;
}

.save-button, .edit-button {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.save-button:hover, .edit-button:hover {
  background: #2980b9;
}

.cancel-button {
  background: #95a5a6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.cancel-button:hover {
  background: #7f8c8d;
}

.delete-button {
  background: #e74c3c;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.delete-button:hover {
  background: #c0392b;
}

.actions-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #ecf0f1;
  display: flex;
  gap: 1rem;
}

.instructions-section {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #3498db;
}

.instructions-section h4 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
}

.instructions-section ol {
  margin: 0;
  padding-left: 1.5rem;
}

.instructions-section li {
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.instructions-section code {
  background: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  color: #e74c3c;
}

.save-file-button {
  background: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.save-file-button:hover:not(:disabled) {
  background: #229954;
}

.save-file-button:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.load-file-button {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.load-file-button:hover {
  background: #2980b9;
}

.message-section {
  margin-top: 1rem;
}

.message {
  padding: 1rem;
  border-radius: 4px;
  font-weight: 600;
}

.message.success {
  background: #d5f4e6;
  color: #27ae60;
  border: 1px solid #27ae60;
}

.message.error {
  background: #fdf2f2;
  color: #e74c3c;
  border: 1px solid #e74c3c;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  
  .form-group {
    min-width: auto;
  }
  
  .table-header, .table-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .table-header > div, .table-row > div {
    border-right: none;
    border-bottom: 1px solid #ddd;
  }
  
  .table-header > div:last-child, .table-row > div:last-child {
    border-bottom: none;
  }
  
  .col-domain::before { content: "Domain: "; font-weight: 600; }
  .col-description::before { content: "Description: "; font-weight: 600; }
  .col-owner::before { content: "Owner: "; font-weight: 600; }
  .col-actions::before { content: "Actions: "; font-weight: 600; }
  
  .actions-section {
    flex-direction: column;
  }
}
</style>
